import React, { useState, useEffect } from 'react';
import { directSupabase, directAuth } from '../lib/supabaseClient';
import {
  Box,
  Button,
  Input,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  FormControl,
  FormLabel,
  Select,
  Option,
} from '@mui/joy';

const AdminUserSetup: React.FC = () => {
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [group, setGroup] = useState('customer');
  const [role, setRole] = useState<string>('');

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const sessionData = await directAuth.getSession();
        
        if (sessionData?.session?.user) {
          setCurrentUser(sessionData.session.user);
          
          // Fetch current user's profile
          const { data: profile, error: profileError } = await directSupabase
            .from('profiles')
            .select('*')
            .eq('id', sessionData.session.user.id)
            .single();
          
          if (profileError) {
            console.error('Error fetching profile:', profileError);
          } else if (profile) {
            setFullName(profile.full_name || '');
            setGroup(profile.group || 'customer');
            setRole(profile.role || '');
          }
        }
      } catch (err) {
        setError('Failed to fetch current user');
      } finally {
        setLoading(false);
      }
    };

    fetchCurrentUser();
  }, []);

  const handleCreateProfile = async () => {
    if (!currentUser) {
      setError('No current user found');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const profileData = {
        id: currentUser.id,
        full_name: fullName,
        email: currentUser.email,
        group: group,
        role: role || null,
      };

      const { error } = await directSupabase
        .from('profiles')
        .insert([profileData])
        .select()
        .single();
      
      if (error) {
        setError(error.message || 'Failed to create profile');
      } else {
        setSuccess('Profile created successfully!');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create profile');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async () => {
    if (!currentUser) {
      setError('No current user found');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const profileData = {
        full_name: fullName,
        group: group,
        role: role || null,
      };

      const { error } = await directSupabase
        .from('profiles')
        .update(profileData)
        .eq('id', currentUser.id);
      
      if (error) {
        setError(error.message || 'Failed to update profile');
      } else {
        setSuccess('Profile updated successfully!');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchByEmail = async () => {
    if (!email) {
      setError('Please enter an email address');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const { data: profile, error } = await directSupabase
        .from('profiles')
        .select('*')
        .eq('email', email)
        .single();
      
      if (error) {
        setError('Profile not found');
      } else if (profile) {
        setFullName(profile.full_name || '');
        setGroup(profile.group || 'customer');
        setRole(profile.role || '');
        setSuccess(`Found profile for ${email}`);
      } else {
        setError('Profile not found');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to search profile');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Typography level="h4" gutterBottom>
        Admin User Setup
      </Typography>

      {error && (
        <Alert color="danger" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert color="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography level="title-lg" gutterBottom>
            Current User Profile
          </Typography>
          {currentUser && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Box flex={1}>
                <FormControl>
                  <FormLabel>Email</FormLabel>
                  <Input
                    value={currentUser.email || ''}
                    disabled
                    sx={{ mb: 2 }}
                  />
                </FormControl>
              </Box>
              <Box flex={1}>
                <FormControl>
                  <FormLabel>User ID</FormLabel>
                  <Input
                    value={currentUser.id || ''}
                    disabled
                    sx={{ mb: 2 }}
                  />
                </FormControl>
              </Box>
              <Box flex={1}>
                <FormControl>
                  <FormLabel>Full Name</FormLabel>
                  <Input
                    value={fullName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFullName(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                </FormControl>
              </Box>
              <Box flex={1}>
                <FormControl>
                  <FormLabel>Group</FormLabel>
                  <Select
                    value={group}
                    onChange={(e: any, value: string | null) => setGroup(value || 'customer')}
                  >
                    <Option value="customer">Customer</Option>
                    <Option value="admin">Admin</Option>
                    <Option value="staff">Staff</Option>
                  </Select>
                </FormControl>
              </Box>
              <Box flex={1}>
                <FormControl>
                  <FormLabel>Role</FormLabel>
                  <Select
                    value={role}
                    onChange={(e: any, value: string | null) => setRole(value || '')}
                  >
                    <Option value="">—</Option>
                    <Option value="admin">Admin</Option>
                    <Option value="document">Document</Option>
                  </Select>
                </FormControl>
              </Box>
            </Stack>
          )}
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button
              variant="solid"
              onClick={handleCreateProfile}
              disabled={loading}
            >
              Create Profile
            </Button>
            <Button
              variant="outlined"
              onClick={handleUpdateProfile}
              disabled={loading}
            >
              Update Profile
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography level="title-lg" gutterBottom>
            Search Profile by Email
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Box flex={2}>
              <FormControl>
                <FormLabel>Email</FormLabel>
                <Input
                  value={email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                  placeholder="Enter email to search"
                />
              </FormControl>
            </Box>
            <Box flex={1} display="flex" alignItems="flex-end">
              <Button
                variant="solid"
                onClick={handleSearchByEmail}
                disabled={loading || !email}
                sx={{ height: '56px', mt: { xs: 2, sm: 0 } }}
              >
                Search
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AdminUserSetup; 